import { useTranslation } from 'react-i18next';
import {
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { Box, Grid } from '@mui/material';
import { getBackendLanguage, translateBackend } from 'assets/i18n/config';
import {
  Button, TranslatableTextField, Typography, SelectField, MenuItem,
} from 'ovComponents';
import { TargetAllocation, TranslatedString } from '../../../../../interfaces';

import { ovAnalyticsEvents } from '../../../../../util/analytics/analytics';
import { AnalyticsContext } from '../../../../../providers/analyticsProvider';
import { AddModelPortfoliosIPSAndAssetClassesOptions } from './addModelPortfoliosIPSAndAssetClasses';
import { AssetClassesTargetAllocations } from './components/assetClassesTargetAllocations';
import { useThemeTokens } from '../../../../../providers/themeTokenProvider';
import { useTextFieldTokens } from '../../../../2-component/textField/textField.tokens';
import TranslatableRichTextEditor from '../../../../3-pattern/translatableRichTextEditor/translatableRichTextEditor';
import { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio';
import { WorkflowCompletionForm } from '../../components/workflowCompletionForm';
import { FieldRequiredType } from '../../../configurableOptionFields/basicField';

export interface AssetClassTargetAllocation extends TargetAllocation {
  actualPercentage: number;
  color: 'supportOne' | 'supportTwo' | 'supportThree' | 'supportFour' | 'supportFive' | 'supportSix';
  fromSecurity: boolean;
  securityPrimaryAssetClassId?: string;
  securitySecondaryAssetClassId?: string;
}

export const AddModelPortfoliosIPSAndAssetClassesVisual = ({
  options,
  workflowCompletion,
  stepLoading,
  modelPortfolioData,
  setModelPortfolioData,
  onSaveForLater,
  onCreateModelPortfolio,
  loading,
}: {
  options: AddModelPortfoliosIPSAndAssetClassesOptions;
  workflowCompletion: any;
  stepLoading: boolean;
  modelPortfolioData: ModelPortfolioData;
  setModelPortfolioData: (data: ModelPortfolioData) => void;
  onSaveForLater: () => void;
  onCreateModelPortfolio: () => void;
  loading: boolean;
}) => {
  const { t } = useTranslation(['modelPortfolios', 'shared', 'components']);
  const [focused, setFocused] = useState<string[]>([]);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [translatedPortfolioDescription, setTranslatedPortfolioDescription] = useState<TranslatedString>(modelPortfolioData?.translatedPortfolioDescription || { en: '', fr: '' });
  const [translatedInvestmentObjective, setTranslatedInvestmentObjective] = useState<TranslatedString>(modelPortfolioData?.translatedInvestmentObjective || { en: '', fr: '' });
  const [translatedLiquidity, setTranslatedLiquidity] = useState<TranslatedString>(modelPortfolioData?.translatedLiquidity || { en: '', fr: '' });
  const [translatedPortfolioDisclaimer, setTranslatedPortfolioDisclaimer] = useState<TranslatedString>(modelPortfolioData?.translatedPortfolioDisclaimer || { en: '', fr: '' });
  const [targetAllocations, setTargetAllocations] = useState<AssetClassTargetAllocation[]>(modelPortfolioData.targetAllocations || []);
  const [groupBy, setGroupBy] = useState<'primary' | 'secondary'>(modelPortfolioData.groupBy || 'primary');
  const { comp } = useThemeTokens(useTextFieldTokens());

  const handleGroupByChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const newGroupBy = e.target.value as 'primary' | 'secondary';
    setGroupBy(newGroupBy);

    // Update targetAllocations based on groupBy change
    const updatedAllocations = targetAllocations.map((allocation) => {
      if (allocation.fromSecurity) {
        return {
          ...allocation,
          secondaryAssetClass: {
            id: newGroupBy === 'primary'
              ? allocation.securityPrimaryAssetClassId
              : allocation.securitySecondaryAssetClassId,
          },
        };
      }
      return allocation;
    });

    setTargetAllocations(updatedAllocations);
    update('targetAllocations', updatedAllocations);
    update('groupBy', newGroupBy);
  };

  const update = useCallback(
    (field: string, value: string | TranslatedString | TargetAllocation[]) => {
      if (typeof value === 'object' && ('en' in value || 'fr' in value)) {
        const translatedValue = value as TranslatedString;
        if (!translatedValue.en && !translatedValue.fr) {
          return;
        }
      }

      const updatedData = {
        ...modelPortfolioData,
        [field]: value,
      };

      setModelPortfolioData(updatedData);
    },
    [modelPortfolioData, setModelPortfolioData],
  );

  const handleTargetAllocationsChange = (newTargetAllocations: AssetClassTargetAllocation[]) => {
    setTargetAllocations(newTargetAllocations);
    update('targetAllocations', newTargetAllocations);
  };

  const validateField = (
    enabled: boolean,
    required: string,
    value: any,
  ): boolean => enabled && required === FieldRequiredType.ERROR && !value;

  const validateTranslatedField = (
    enabled: boolean,
    required: string,
    value: TranslatedString | undefined,
  ): boolean => enabled && required === FieldRequiredType.ERROR && (!value || !value[getBackendLanguage()]);

  const validateFields = (returnFields = false) => {
    const fields: string[] = [];

    if (options.ips?.enabled) {
      if (validateTranslatedField(
        options.ips.options.description.enabled,
        options.ips.options.description.required,
        translatedPortfolioDescription,
      )) {
        fields.push('description');
      }

      if (validateTranslatedField(
        options.ips.options.objective.enabled,
        options.ips.options.objective.required,
        translatedInvestmentObjective,
      )) {
        fields.push('objective');
      }

      if (validateTranslatedField(
        options.ips.options.liquidity.enabled,
        options.ips.options.liquidity.required,
        translatedLiquidity,
      )) {
        fields.push('liquidity');
      }
    }

    if (validateField(
      options.disclaimer?.enabled,
      options.disclaimer?.required,
      translatedPortfolioDisclaimer,
    )) {
      fields.push('disclaimer');
    }

    if (validateField(
      options.assetClassesDistribution?.enabled,
      options.assetClassesDistribution?.required,
      targetAllocations.length,
    )) {
      fields.push('assetClassesDistribution');
    }

    if (returnFields) {
      setFocused(fields);
      return fields.length === 0;
    }
    return fields.length === 0;
  };

  const isDataValid = () => validateFields(false);

  const validate = () => validateFields(true);

  const isValidTargetAllocations = useMemo(() => targetAllocations?.every((item) => !(
    item.secondaryAssetClass?.id?.toString().length === 10
    || item.min > item.max
    || item.min < 0
    || item.max > 100
    || (item.min === 0 && item.max === 0)
  )), [targetAllocations]);

  const submit = async () => {
    if (validate()) {
      sendAnalytic(ovAnalyticsEvents.workflowsAddModelPortfolioDetailsContinueButtonSelect, {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      });
      await onCreateModelPortfolio();
    }
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('shared:done'),
        disabled: stepLoading || !isDataValid() || !isValidTargetAllocations,
        dataTestId: 'add-model-portfolio-ips-continue-button',
      }}
      buttonFooter={
        <Box display="flex" justifyContent="end" alignItems="flex-end">
          {options?.buttonSaveForLater?.enabled && (
            <Button
              dataTestId="add-model-portfolio-ips-save-for-later-button"
              label={translateBackend(options?.buttonSaveForLater?.label)}
              variant="tonal"
              disabled={stepLoading || !isDataValid() || !isValidTargetAllocations}
              sx={{ mr: 2, textAlign: 'center' }}
              onClick={onSaveForLater}
              loading={loading}
            />
          )}
        </Box>
      }
    >
      {options?.title && (
        <Typography
          variant="displayLarge"
          data-testid="add-model-portfolio-ips-title"
          sx={{
            mt: 1,
            mb: 2,
            fontWeight: 400,
            fontSize: '40px',
            fontFamily: 'Tiempos Text',
          }}>
          {translateBackend(options?.title)}
        </Typography>
      )}

      <Grid container spacing={2}>

        {options?.ips?.options?.description?.enabled && (
          <Grid item xs={12}>
            <TranslatableTextField
              testId="ips-description"
              onChange={(value) => {
                setTranslatedPortfolioDescription(value);
                update('translatedPortfolioDescription', value);
                setFocused([...focused, 'description']);
              }}
              disabled={stepLoading}
              label={translateBackend(options?.ips?.options?.description?.label)}
              fullWidth
              multiline
              rows={1}
              value={translatedPortfolioDescription}
              onBlur={() => setFocused([...focused, 'description'])}
              error={!translatedPortfolioDescription[getBackendLanguage()] && focused.includes('description')}
              required={options.ips.options.description.required === FieldRequiredType.ERROR}
            />
          </Grid>
        )}

        {options?.ips?.options?.objective?.enabled && (
          <Grid item xs={12}>
            <TranslatableTextField
              testId="ips-objective"
              onChange={(value) => {
                setTranslatedInvestmentObjective(value);
                update('translatedInvestmentObjective', value);
                setFocused([...focused, 'objective']);
              }}
              disabled={stepLoading}
              label={translateBackend(options?.ips?.options?.objective?.label)}
              fullWidth
              multiline
              rows={1}
              value={translatedInvestmentObjective}
              onBlur={() => setFocused([...focused, 'objective'])}
              error={!translatedInvestmentObjective[getBackendLanguage()] && focused.includes('objective')}
              required={options.ips.options.objective.required === FieldRequiredType.ERROR}
            />
          </Grid>
        )}

        {options?.ips?.options?.liquidity?.enabled && (
          <Grid item xs={12}>
            <TranslatableTextField
              testId="ips-liquidity"
              onChange={(value) => {
                setTranslatedLiquidity(value);
                update('translatedLiquidity', value);
                setFocused([...focused, 'liquidity']);
              }}
              disabled={stepLoading}
              label={translateBackend(options?.ips?.options?.liquidity?.label)}
              fullWidth
              multiline
              rows={1}
              value={translatedLiquidity}
              onBlur={() => setFocused([...focused, 'liquidity'])}
              required={options.ips.options.liquidity.required === FieldRequiredType.ERROR}
            />
          </Grid>
        )}

        {options?.disclaimer?.enabled && (
          <Grid item xs={12}>
            <TranslatableRichTextEditor
              testId="ips-disclaimer-workflow-substep"
              value={translatedPortfolioDisclaimer}
              setValue={(value) => {
                setTranslatedPortfolioDisclaimer(value);
                update('translatedPortfolioDisclaimer', value);
                setFocused([...focused, 'disclaimer']);
              }}
              label={translateBackend(options?.disclaimer?.label)}
              fullToolbar={false}
            />
          </Grid>
        )}

        {options?.assetClassesDistribution?.enabled && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <SelectField
                testId="group-by-select"
                label="Group By"
                value={groupBy}
                onChange={handleGroupByChange}
                fullWidth
              >
                <MenuItem
                  data-testid="group-by-select-primary"
                  value="primary"
                >
                  {t('components:productShelf.exportHeaders.primaryAssetClass')}
                </MenuItem>
                <MenuItem
                  data-testid="group-by-select-secondary"
                  value="secondary"
                >
                  {t('components:productShelf.exportHeaders.secondaryAssetClass')}
                </MenuItem>
              </SelectField>
            </Box>
            <Typography
              data-testid="ips-asset-classes-distribution-label"
              variant='labelSmall'
              sx={{ mb: '4px', color: comp.textField.labelColor }}>
              {translateBackend(options?.assetClassesDistribution?.label)}
              {options.assetClassesDistribution.required === FieldRequiredType.ERROR && ' *'}
            </Typography>
            <AssetClassesTargetAllocations
              testId="ips-asset-classes-distribution"
              targetAllocations={targetAllocations}
              setTargetAllocations={handleTargetAllocationsChange}
              securities={modelPortfolioData?.securities}
            />
          </Grid>
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default AddModelPortfoliosIPSAndAssetClassesVisual;
